import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import { X, AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react'
import { useFacility } from '../context/FacilityContext'

/* Felles byggeklosser for partnerportalen (Spelaren 2.0). Stilene ligger i src/index.css. */

const cx = (...parts) => parts.filter(Boolean).join(' ')

/* ---------- Button ---------- */
export const Button = React.forwardRef(
  ({ variant = 'primary', size, block, icon: Icon, iconSize, className, children, type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cx('btn', `btn-${variant}`, size && `btn-${size}`, block && 'btn-block', className)}
      {...rest}
    >
      {Icon && <Icon size={iconSize || (size === 'sm' || size === 'xs' ? 16 : 18)} strokeWidth={2} aria-hidden="true" />}
      {children}
    </button>
  )
)

export const IconButton = ({ icon: Icon, label, size, danger, className, iconSize, type = 'button', ...rest }) => (
  <button
    type={type}
    aria-label={label}
    title={label}
    className={cx('icon-btn', size, danger && 'danger', className)}
    {...rest}
  >
    <Icon size={iconSize || (size === 'sm' ? 16 : 18)} strokeWidth={1.9} aria-hidden="true" />
  </button>
)

/* ---------- PageHeader ---------- */
// eyebrow: liten tekst over tittelen. Standard er navnet på aktiv arena.
export const PageHeader = ({ title, subtitle, eyebrow, actions, children }) => {
  const facility = useFacility()
  const eb = eyebrow !== undefined ? eyebrow : facility?.activeFacility?.name
  return (
    <div className="page-header">
      <div className="page-header-text">
        {eb && <span className="page-header-eyebrow">{eb}</span>}
        <h1 className="page-header-title">{title}</h1>
        {subtitle && <span className="page-header-subtitle">{subtitle}</span>}
        {children}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
  )
}

export const SectionHead = ({ title, subtitle, actions, as: Tag = 'h2', id }) => (
  <div className="section-head">
    <div className="section-head-text">
      <Tag className="section-title" id={id}>{title}</Tag>
      {subtitle && <span className="section-sub">{subtitle}</span>}
    </div>
    {actions && <div className="row">{actions}</div>}
  </div>
)

/* ---------- StatTile ---------- */
export const StatTile = ({ label, value, sub, to, onClick, as }) => {
  const content = (
    <>
      <span className="stat-tile-label">{label}</span>
      <span className="stat-tile-value">{value}</span>
      {sub && <span className="stat-tile-sub">{sub}</span>}
    </>
  )
  if (as) {
    const As = as
    return <As className="stat-tile" to={to} onClick={onClick}>{content}</As>
  }
  if (onClick) {
    return <button type="button" className="stat-tile" onClick={onClick} style={{ textAlign: 'left', cursor: 'pointer' }}>{content}</button>
  }
  return <div className="stat-tile">{content}</div>
}

/* ---------- StatusPill ---------- */
// tone: open | live | signal | neutral | muted | danger | brand | outline
export const StatusPill = ({ tone = 'neutral', dot, large, children, className, ...rest }) => (
  <span className={cx('pill', `pill-${tone}`, dot && 'pill-dot', large && 'pill-lg', className)} {...rest}>
    {children}
  </span>
)

/* Turneringsstatus → norsk etikett og tone (samme ordvalg som appen). */
export const tournamentStatus = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'OPEN':
    case 'REGISTRATION':
    case 'REGISTRATION_OPEN':
      return { label: 'Åpen', tone: 'open' }
    case 'IN_PROGRESS':
    case 'ONGOING':
    case 'STARTED':
    case 'ACTIVE':
      return { label: 'Pågår', tone: 'live' }
    case 'COMPLETED':
    case 'FINISHED':
      return { label: 'Fullført', tone: 'muted' }
    case 'CANCELLED':
    case 'CANCELED':
      return { label: 'Avlyst', tone: 'danger' }
    case 'DRAFT':
      return { label: 'Utkast', tone: 'muted' }
    case 'CLOSED':
    case 'REGISTRATION_CLOSED':
      return { label: 'Påmelding stengt', tone: 'neutral' }
    case 'FULL':
      return { label: 'Fullt', tone: 'neutral' }
    default:
      return { label: status || '–', tone: 'neutral' }
  }
}

/* ---------- Alert ---------- */
const ALERT_ICON = { success: CheckCircle2, error: AlertCircle, warning: AlertTriangle, info: Info, note: Info }
export const Alert = ({ tone = 'info', children, icon = true, className, ...rest }) => {
  const Icon = ALERT_ICON[tone] || Info
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('alert', `alert-${tone}`, className)}
      {...rest}
    >
      {icon && <Icon size={18} strokeWidth={1.9} aria-hidden="true" />}
      <div>{children}</div>
    </div>
  )
}

/* ---------- EmptyState ---------- */
export const EmptyState = ({ icon: Icon, title, children, action, compact }) => (
  <div className={cx('empty-state', compact && 'compact')}>
    {Icon && (
      <span className="empty-state-icon">
        <Icon size={22} strokeWidth={1.9} aria-hidden="true" />
      </span>
    )}
    {title && <span className="empty-state-title">{title}</span>}
    {children && <span>{children}</span>}
    {action}
  </div>
)

export const Loading = ({ children = 'Laster…' }) => (
  <div className="loading-block" role="status">
    <span className="spinner" aria-hidden="true" />
    <span>{children}</span>
  </div>
)

/* ---------- Field ---------- */
export const Field = ({ label, hint, error, children, className, style, htmlFor }) => (
  <label className={cx('field', className)} style={style} htmlFor={htmlFor}>
    {label && <span className="field-label">{label}</span>}
    {children}
    {hint && <span className="field-hint">{hint}</span>}
    {error && <span className="field-error">{error}</span>}
  </label>
)

/* ---------- Tabs ---------- */
// tabs: [{ key, label, count? }]
export const Tabs = ({ tabs, active, onChange, label }) => {
  const refs = useRef({})
  const onKey = (e, idx) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const next = (idx + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
    onChange(tabs[next].key)
    refs.current[tabs[next].key]?.focus()
  }
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.key}
          ref={(el) => { refs.current[t.key] = el }}
          type="button"
          role="tab"
          className="tab"
          aria-selected={active === t.key}
          tabIndex={active === t.key ? 0 : -1}
          onClick={() => onChange(t.key)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.icon && <t.icon size={17} strokeWidth={1.9} aria-hidden="true" />}
          {t.label}
          {t.count !== undefined && t.count !== null && <span className="tab-count">{t.count}</span>}
        </button>
      ))}
    </div>
  )
}

/* ---------- DataTable ---------- */
// columns: [{ key, header, render?(row), className?, align?: 'right' }]
export const DataTable = ({ columns, rows, rowKey = 'id', minWidth = 640, footer, caption, onRowClick }) => (
  <div className="table-wrap">
    <table className="data-table" style={{ minWidth }}>
      {caption && <caption className="sr-only">{caption}</caption>}
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={cx(c.align === 'right' && 'num', c.headerClassName)} scope="col">
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={typeof rowKey === 'function' ? rowKey(r, i) : r[rowKey] ?? i}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            style={onRowClick ? { cursor: 'pointer' } : undefined}
          >
            {columns.map((c) => (
              <td key={c.key} className={cx(c.align === 'right' && 'num', c.className)}>
                {c.render ? c.render(r, i) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {footer && <tfoot>{footer}</tfoot>}
    </table>
  </div>
)

/* ---------- Modal ---------- */
export const Modal = ({ open = true, onClose, title, subtitle, size, children, footer, labelledBy }) => {
  const id = useId()
  const panelRef = useRef(null)
  useEffect(() => {
    if (!open) return
    const prev = document.activeElement
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose() }
    document.addEventListener('keydown', onKey)
    const first = panelRef.current?.querySelector('input, select, textarea, button:not([data-modal-close])')
    ;(first || panelRef.current)?.focus?.()
    return () => {
      document.removeEventListener('keydown', onKey)
      prev?.focus?.()
    }
  }, [open, onClose])
  if (!open) return null
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose() }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || `${id}-t`}
        className={cx('modal-panel', size)}
        tabIndex={-1}
      >
        {(title || onClose) && (
          <div className="modal-head">
            <div>
              {title && <h2 className="modal-title" id={`${id}-t`}>{title}</h2>}
              {subtitle && <div className="modal-sub">{subtitle}</div>}
            </div>
            {onClose && (
              <button type="button" className="icon-btn lg" aria-label="Lukk" onClick={onClose} data-modal-close>
                <X size={18} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {children}
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}

/* ---------- ConfirmDialog + useConfirm ---------- */
export const ConfirmDialog = ({ open, title, message, confirmLabel = 'OK', cancelLabel = 'Avbryt', danger, onConfirm, onCancel }) => (
  <Modal open={open} onClose={onCancel} title={title} size="sm">
    <p className="confirm-text">{message}</p>
    <div className="modal-foot">
      <Button variant="tertiary" onClick={onCancel}>{cancelLabel}</Button>
      <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} autoFocus>{confirmLabel}</Button>
    </div>
  </Modal>
)

const ConfirmContext = createContext(null)

// Erstatter window.confirm. Bruk: const confirm = useConfirm(); if (!(await confirm('Tekst'))) return
// eller confirm({ message, title, confirmLabel, danger }).
export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null)
  const confirm = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : opts
    return new Promise((resolve) => setState({ ...o, resolve }))
  }, [])
  const close = (val) => {
    state?.resolve(val)
    setState(null)
  }
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          open
          title={state.title || 'Bekreft'}
          message={state.message}
          confirmLabel={state.confirmLabel || (state.danger ? 'Bekreft' : 'OK')}
          cancelLabel={state.cancelLabel}
          danger={state.danger}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext)
  return ctx || ((opts) => Promise.resolve(window.confirm(typeof opts === 'string' ? opts : opts.message)))
}

export const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
